import {useCallback, useEffect, useMemo, useState} from 'react';

import {Box, Stack, Typography} from '@mui/material';

import {C} from '../contentGenerator/constants';
import {Field, FMultiSelect, FSelect} from '../contentGenerator/components/ui';
import {productService} from '../contentGenerator/services/products/productService';
import {useProductContext} from './ProductContext';

const SOURCE_META = {
    demo: {badgeText: 'Demo fallback catalog', accent: C.amber, background: C.amberL, border: `${C.amber}44`},
    shopify: {badgeText: 'Shopify-synced catalog', accent: C.teal, background: C.tealL, border: `${C.teal}44`},
};

const areValuesEqual = (left = [], right = []) =>
    left.length === right.length && left.every((value, index) => value === right[index]);

export default function ProductSelector() {
    const {
        collectionId, setCollectionId,
        productOptions,
        productsLoading,
        productSource, lastSyncedAt,
        syncProducts,
        selectedProductIds,
        currentProductIndex, setCurrentProductIndex,
        selectedProducts,
        handleProductChange,
        groupFilteredProducts,
        reviewGroupByProductId,
    } = useProductContext();

    const [collections, setCollections] = useState([]);
    const [collectionsLoading, setCollectionsLoading] = useState(false);
    const [filterState, setFilterState] = useState({
        filterText: '',
        filteredValues: [],
    });

    // Fetch collections on mount
    useEffect(() => {
        let cancelled = false;
        (async () => {
            setCollectionsLoading(true);
            try {
                const result = await productService.getCollections();
                if (!cancelled) setCollections(result);
            } catch {
                // Collections failed — user can still use "All products"
            } finally {
                if (!cancelled) setCollectionsLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const collectionOptions = useMemo(() => [
        {label: 'All products', value: ''},
        ...collections.map((collection) => ({
            label: collection.title,
            value: String(collection.id),
            searchText: `${collection.title || ''} ${collection.id || ''}`,
        })),
    ], [collections]);

    const sourceMeta = SOURCE_META[productSource] || SOURCE_META.demo;
    const handleFilterStateChange = useCallback(({filterText = '', filteredValues = []}) => {
        setFilterState((currentState) => {
            if (
                currentState.filterText === filterText &&
                areValuesEqual(currentState.filteredValues, filteredValues)
            ) {
                return currentState;
            }
            return {filterText, filteredValues};
        });
    }, []);

    const filteredSelectedProductIds = useMemo(() => {
        const filteredValueSet = new Set(filterState.filteredValues);
        return selectedProductIds.filter((productId) => filteredValueSet.has(productId));
    }, [filterState.filteredValues, selectedProductIds]);

    const canGroupFiltered =
        filterState.filterText.trim() &&
        filteredSelectedProductIds.length > 1;

    // Check if filtered products already share a common group
    const filteredGroupStatus = useMemo(() => {
        if (!canGroupFiltered) return null;
        const groups = filteredSelectedProductIds
            .map((pid) => reviewGroupByProductId[pid])
            .filter(Boolean);
        if (!groups.length) return null;
        const uniqueGroups = new Set(groups);
        if (uniqueGroups.size === 1 && groups.length === filteredSelectedProductIds.length) {
            return groups[0]; // all share the same group
        }
        return null;
    }, [canGroupFiltered, filteredSelectedProductIds, reviewGroupByProductId]);

    const filterActions = useMemo(
        () => [
            {
                key: 'group-filtered',
                label: filteredGroupStatus
                    ? `Grouped: ${filteredGroupStatus}`
                    : 'Group filtered',
                disabled: !canGroupFiltered,
                onClick: () =>
                    groupFilteredProducts({
                        filterText: filterState.filterText,
                        productIds: filteredSelectedProductIds,
                    }),
                sx: filteredGroupStatus
                    ? {
                        border: `1px solid ${C.teal}44`,
                        color: C.teal,
                        backgroundColor: C.tealL,
                    }
                    : {
                        border: `1px solid ${canGroupFiltered ? `${C.amber}44` : C.border}`,
                        color: canGroupFiltered ? C.amber : C.textMuted,
                        backgroundColor: canGroupFiltered ? C.amberL : 'transparent',
                    },
            },
        ],
        [canGroupFiltered, filteredGroupStatus, filterState.filterText, filteredSelectedProductIds, groupFilteredProducts],
    );

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
            {/* Collection / Sync */}
            <Box sx={{display: 'grid', gridTemplateColumns: '1fr auto', gap: '9px'}}>
                <Field label="Collection" sx={{maxWidth: 300}}>
                    <FSelect
                        value={collectionId}
                        onChange={(event) => setCollectionId(event.target.value)}
                        options={collectionOptions}
                        disabled={collectionsLoading}
                        filterable
                        filterPlaceholder="Filter collections"
                        noMatchesLabel="No collections match that filter"
                        menuMaxHeight={300}
                    />
                </Field>
                <Field label="Sync">
                    <button
                        onClick={syncProducts}
                        disabled={productsLoading}
                        style={{
                            fontFamily: 'inherit',
                            fontSize: 12,
                            fontWeight: 500,
                            // Match MUI small-Select height (40px) so the
                            // sync button bottom-aligns with the collection
                            // dropdown to its left.
                            height: 40,
                            padding: '0 12px',
                            borderRadius: 8,
                            border: `1px solid ${sourceMeta.border}`,
                            background: sourceMeta.background,
                            color: sourceMeta.accent,
                            cursor: productsLoading ? 'not-allowed' : 'pointer',
                            minWidth: 88,
                        }}>
                        {productsLoading ? 'Syncing...' : 'Sync Shopify'}
                    </button>
                </Field>
            </Box>

            {/* Product selector */}
            <Field label="Product">
                <FMultiSelect
                    value={selectedProductIds}
                    onChange={handleProductChange}
                    options={productOptions.map((product) => ({
                        value: product.id,
                        label: product.sku || product.id,
                        searchText: `${product.sku || product.id} ${product.name || ''}`,
                    }))}
                    placeholder="Select SKUs"
                    emptyLabel="No SKUs available"
                    filterable
                    filterMode="sql-like"
                    filterPlaceholder="Filter SKUs, e.g. DEMO%"
                    filterHint="Use SQL LIKE-style patterns. `%` matches any sequence and `_` matches one character."
                    noMatchesLabel="No SKUs match that filter"
                    menuWidth={280}
                    menuMaxHeight={280}
                    onFilterStateChange={handleFilterStateChange}
                    filterActions={filterActions}
                />
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{fontSize: 9.5, color: C.textMuted}}>
                    <Typography component="span" sx={{fontSize: 9.5, color: sourceMeta.accent}}>
                        {sourceMeta.badgeText}
                    </Typography>
                    <Typography component="span" sx={{fontSize: 9.5, color: C.textMuted}}>
                        {selectedProductIds.length} selected · {productOptions.length} SKUs
                        {lastSyncedAt ? ` · ${lastSyncedAt.toLocaleTimeString()}` : ''}
                    </Typography>
                </Stack>
            </Field>

            {/* Prev / Next for multiple selected products */}
            {selectedProducts.length > 1 && (
                <Stack direction="row" alignItems="center" gap="8px">
                    <button
                        onClick={() => setCurrentProductIndex(Math.max(0, currentProductIndex - 1))}
                        disabled={currentProductIndex <= 0}
                        style={{
                            fontFamily: 'inherit',
                            fontSize: 11,
                            fontWeight: 600,
                            padding: '4px 10px',
                            borderRadius: 6,
                            border: `1px solid ${C.border}`,
                            background: 'transparent',
                            color: C.textSoft,
                            cursor: currentProductIndex <= 0 ? 'default' : 'pointer',
                        }}>
                        ‹ Prev
                    </button>
                    <Typography sx={{fontSize: 11, color: C.textSoft}}>
                        {currentProductIndex + 1} / {selectedProducts.length}
                    </Typography>
                    <button
                        onClick={() => setCurrentProductIndex(Math.min(selectedProducts.length - 1, currentProductIndex + 1))}
                        disabled={currentProductIndex >= selectedProducts.length - 1}
                        style={{
                            fontFamily: 'inherit',
                            fontSize: 11,
                            fontWeight: 600,
                            padding: '4px 10px',
                            borderRadius: 6,
                            border: `1px solid ${C.border}`,
                            background: 'transparent',
                            color: C.textSoft,
                            cursor: currentProductIndex >= selectedProducts.length - 1 ? 'default' : 'pointer',
                        }}>
                        Next ›
                    </button>
                </Stack>
            )}
        </Box>
    );
}
